import { useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { ApiError, userFacingMessage } from "../api/errors";
import { useAuth } from "../auth/AuthContext";
import { Button } from "../components/Button";
import { ErrorMessage } from "../components/ErrorMessage";
import { LoadingIndicator } from "../components/LoadingIndicator";
import { TextInput } from "../components/TextInput";

export function LoginPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [demoBusy, setDemoBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (auth.status === "loading") {
    return (
      <div className="hw-auth-screen">
        <LoadingIndicator label="Проверка сессии…" />
      </div>
    );
  }

  if (auth.status === "authenticated") {
    return <Navigate to="/catalog" replace />;
  }

  if (auth.status === "error") {
    return (
      <div className="hw-auth-screen">
        <div className="hw-auth-panel">
          <h1 className="hw-auth-panel__brand">Hot Wheels Collection</h1>
          <ErrorMessage>{auth.message}</ErrorMessage>
          <Button
            onClick={() => {
              void auth.retryBootstrap();
            }}
          >
            Повторить
          </Button>
        </div>
      </div>
    );
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await auth.login(username, password);
      void navigate("/catalog", { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.code === "INVALID_CREDENTIALS") {
        setError("Неверный логин или пароль.");
      } else {
        setError(userFacingMessage(err));
      }
    } finally {
      setSubmitting(false);
      setPassword("");
    }
  }

  async function onStartDemo() {
    setError(null);
    setDemoBusy(true);
    try {
      await auth.startDemo();
      void navigate("/catalog", { replace: true });
    } catch (err) {
      setError(userFacingMessage(err));
    } finally {
      setDemoBusy(false);
    }
  }

  return (
    <div className="hw-auth-screen">
      <div className="hw-auth-panel">
        <h1 className="hw-auth-panel__brand">Hot Wheels Collection</h1>
        <p className="hw-auth-panel__lead">
          Войдите, чтобы открыть каталог и коллекцию.
        </p>
        <form className="hw-auth-form" onSubmit={onSubmit} noValidate>
          <TextInput
            id="login-username"
            label="Имя пользователя"
            name="username"
            autoComplete="username"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            required
            disabled={submitting}
          />
          <TextInput
            id="login-password"
            label="Пароль"
            name="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
            disabled={submitting}
          />
          {error ? <ErrorMessage>{error}</ErrorMessage> : null}
          <Button type="submit" disabled={submitting || demoBusy || !username || !password}>
            {submitting ? "Вход…" : "Войти"}
          </Button>
        </form>
        <div className="hw-auth-panel__secondary">
          <p className="hw-auth-panel__hint">Временная тестовая коллекция</p>
          <Button
            type="button"
            variant="ghost"
            disabled={submitting || demoBusy}
            onClick={() => {
              void onStartDemo();
            }}
          >
            {demoBusy ? "Открываем демо…" : "Попробовать демо"}
          </Button>
        </div>
      </div>
    </div>
  );
}
